/**
 * Dimensions drawn by hand, kept in this browser.
 *
 * SINCE 2026-09-11 THIS IS THE FALLBACK. Dimensions are saved with the survey
 * and shared with the team (`/api/surveys/:id/plan-dimensions`, migration
 * 0039, through `use-plan-dimensions.ts`). This store is what the plan uses
 * only where it has no API to save to (a plan drawn without one, an API older
 * than the route) — and it is where the dimensions drawn before that date
 * still sit: on first load against the API, `use-plan-dimensions.ts` moves
 * each one found here to the survey, under its id here, and deletes it here
 * once the survey holds it.
 *
 * WHERE THEY LIVE, AND WHY THAT MATTERS. In this browser's `localStorage` and
 * nowhere else. The consequence is stated on screen rather than hidden here,
 * because a surveyor who believes a colleague can see the dimension they just
 * drew, and is wrong, is worse off than one who knows the limit: a dimension
 * added on a laptop is invisible on the same person's phone, invisible to the
 * designer picking the survey up, and gone when the browser's data is cleared.
 *
 * KINDS. A length (two points), an angle (three: two legs meeting at the
 * middle one) or an area (three or more, closed). `start` and `end` are always
 * the first and last points, so a record written before angles and areas
 * existed — a length with no `kind` and no `points` — reads exactly as it did.
 *
 * FAILURE. Every read and write is wrapped: `localStorage` throws outright in
 * private browsing on some engines, and on a quota error mid-session. Neither
 * is allowed to take the plan down, so the module degrades to an in-memory
 * store that behaves identically for the rest of the session and simply does
 * not survive a reload.
 *
 * TRUST. What comes back out of storage is not trusted. It was written by this
 * code, but it can also have been hand-edited, half-written by a crash, or left
 * behind by an older shape of this feature — so every record is validated field
 * by field and anything malformed is dropped rather than rendered as a
 * dimension at NaN.
 */

export interface PlanDimensionPoint {
  readonly x: number;
  readonly y: number;
}

export interface PlanDimension {
  readonly id: string;
  /** The storey it was drawn on; a dimension only shows on its own storey. */
  readonly story: number;
  /** Both ends, in plan metres — the same frame `@sts/geometry` projects to. */
  readonly start: PlanDimensionPoint;
  readonly end: PlanDimensionPoint;
  /** A short note the surveyor typed. Absent, never empty. */
  readonly label?: string;
  /** Absent for a length (the original shape). */
  readonly kind?: "angle" | "area";
  /** An angle's three points or an area's corners. Absent for a length: start and end are its points. */
  readonly points?: readonly PlanDimensionPoint[];
}

export type PlanDimensionKind = "line" | "angle" | "area";

export function kindOf(dimension: Pick<PlanDimension, "kind">): PlanDimensionKind {
  return dimension.kind ?? "line";
}

/** Every point a dimension measures between, in order. */
export function pointsOf(dimension: Pick<PlanDimension, "start" | "end" | "points">): readonly PlanDimensionPoint[] {
  return dimension.points ?? [dimension.start, dimension.end];
}

/** A dimension of `kind` through `points`, in the stored shape. */
export function dimensionOf(
  id: string,
  story: number,
  kind: PlanDimensionKind,
  points: readonly PlanDimensionPoint[],
  label?: string | null,
): PlanDimension {
  const first = points[0] as PlanDimensionPoint;
  const last = points[points.length - 1] as PlanDimensionPoint;
  return {
    id,
    story,
    start: first,
    end: last,
    ...(label ? { label } : {}),
    ...(kind === "line" ? {} : { kind, points: [...points] }),
  };
}

const PREFIX = "sts.survey-plan-dimensions.v1:";

/**
 * The fallback store, and the fact we are using it. `degraded` latches: once
 * storage has thrown, every later call goes to memory rather than throwing
 * again on every keystroke.
 */
const memory = new Map<string, readonly PlanDimension[]>();
let degraded = false;

/** True once storage has failed and annotations are session-only. */
export function isMemoryOnly(): boolean {
  return degraded;
}

function store(): Storage | undefined {
  if (degraded) return undefined;
  try {
    // Reading the property itself throws in some locked-down configurations,
    // which is why this is inside the try rather than a plain `typeof` check.
    const candidate = globalThis.localStorage;
    return candidate ?? undefined;
  } catch {
    degraded = true;
    return undefined;
  }
}

function isPoint(value: unknown): value is PlanDimensionPoint {
  if (typeof value !== "object" || value === null) return false;
  const point = value as Record<string, unknown>;
  return (
    typeof point["x"] === "number" &&
    Number.isFinite(point["x"]) &&
    typeof point["y"] === "number" &&
    Number.isFinite(point["y"])
  );
}

function isDimension(value: unknown): value is PlanDimension {
  if (typeof value !== "object" || value === null) return false;
  const dimension = value as Record<string, unknown>;
  return (
    typeof dimension["id"] === "string" &&
    dimension["id"].length > 0 &&
    typeof dimension["story"] === "number" &&
    Number.isFinite(dimension["story"]) &&
    isPoint(dimension["start"]) &&
    isPoint(dimension["end"]) &&
    (dimension["label"] === undefined || typeof dimension["label"] === "string") &&
    (dimension["kind"] === undefined ||
      ((dimension["kind"] === "angle" || dimension["kind"] === "area") &&
        Array.isArray(dimension["points"]) &&
        (dimension["points"] as unknown[]).length >= 3 &&
        (dimension["points"] as unknown[]).every(isPoint)))
  );
}

function parse(raw: string | null): readonly PlanDimension[] {
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isDimension);
  } catch {
    // Corrupt JSON. Returning nothing is right: the alternative is throwing on
    // every render of the survey page for as long as the bad value sits there.
    return [];
  }
}

/** Every dimension saved for one survey, oldest first. */
export function list(surveyId: string): readonly PlanDimension[] {
  const storage = store();
  if (storage) {
    try {
      const saved = parse(storage.getItem(PREFIX + surveyId));
      // Mirrored into memory, so that if storage fails later in the session
      // the fallback starts from what was on screen rather than from nothing.
      memory.set(surveyId, saved);
      return saved;
    } catch {
      degraded = true;
    }
  }
  return memory.get(surveyId) ?? [];
}

/**
 * Calls `onChange` when another tab of this browser changes this survey's
 * dimensions — the `storage` event fires only in the OTHER tabs, which is
 * exactly the case to cover. Returns the unsubscribe function.
 *
 * This is the whole of the sharing this module can offer: tabs of one browser
 * profile on one machine. Nothing here reaches a colleague.
 */
export function subscribe(surveyId: string, onChange: () => void): () => void {
  const target = globalThis as { addEventListener?: Window["addEventListener"]; removeEventListener?: Window["removeEventListener"] };
  if (typeof target.addEventListener !== "function") return () => {};
  const handler = (event: StorageEvent): void => {
    if (event.key === null || event.key === PREFIX + surveyId) onChange();
  };
  target.addEventListener("storage", handler);
  return () => target.removeEventListener?.("storage", handler);
}

function save(surveyId: string, next: readonly PlanDimension[]): readonly PlanDimension[] {
  memory.set(surveyId, next);
  const storage = store();
  if (storage) {
    try {
      storage.setItem(PREFIX + surveyId, JSON.stringify(next));
    } catch {
      // Full, or refused. The session keeps working from memory and the caller
      // is told through `isMemoryOnly()`, which the plan surfaces on screen.
      degraded = true;
    }
  }
  return next;
}

/** Adds one dimension and returns the new list. */
export function add(
  surveyId: string,
  dimension: PlanDimension,
): readonly PlanDimension[] {
  return save(surveyId, [...list(surveyId), dimension]);
}

/** Removes one dimension by id and returns the new list. */
export function remove(surveyId: string, id: string): readonly PlanDimension[] {
  return save(
    surveyId,
    list(surveyId).filter((dimension) => dimension.id !== id),
  );
}

/** Sets or clears a dimension's label. An empty string clears it. */
export function setLabel(
  surveyId: string,
  id: string,
  label: string,
): readonly PlanDimension[] {
  const trimmed = label.trim();
  return save(
    surveyId,
    list(surveyId).map((dimension) => {
      if (dimension.id !== id) return dimension;
      const { label: _dropped, ...rest } = dimension;
      return trimmed === "" ? rest : { ...rest, label: trimmed };
    }),
  );
}

/**
 * An id for a new dimension.
 *
 * `crypto.randomUUID` is not present on http:// origins in some browsers and
 * not in every test environment, so there is a fallback — these ids never leave
 * this browser, so uniqueness within one survey is the whole requirement.
 */
export function newDimensionId(): string {
  try {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (uuid) return uuid;
  } catch {
    // Fall through.
  }
  return `dim-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
